import type {CommentState} from 'atoms/comment';
import {DocumentThread} from 'client';
import commentStyles from 'components/DSYS/editor/plugins/Comment.module.css';
import {
  COMMENT_DISPLAY_COMPACT_WIDTH_PX,
  COMMENT_DISPLAY_WIDTH_PX,
} from 'components/DSYS/editor/plugins/commentDisplaySizing';
import {CommentDisplay} from 'components/DSYS/editor/plugins/comments/CommentDisplay';
import {COMMENT_EDITOR_KEY_ATTRIBUTE} from 'components/DSYS/editor/utils/commentEditorRoots';
import {
  getPlanningCommentOriginIdFromElement,
  PLANNING_COMMENT_ORIGIN_ATTR,
} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {type CSSProperties, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {findCommentPositionAnchor} from './commentsHelpers';
import styles from './Planning.module.css';
import {useCommentsPanelHoveredThread} from './useCommentsPanelHoveredThread';

const COMMENT_SIZING_VARS = {
  '--comment-display-width': `${COMMENT_DISPLAY_WIDTH_PX}px`,
  '--comment-display-compact-width': `${COMMENT_DISPLAY_COMPACT_WIDTH_PX}px`,
} as CSSProperties;

// Threads whose highlights are within this vertical distance (px) are grouped on the same row
const SAME_LINE_TOLERANCE_PX = 6;
// Vertical offset between stacked items within the same group
const STACK_OFFSET_PX = 48;
// Visual gap (px) between the bottom edge of one comment and the top edge of
// the next. This single constant governs all spacing — between collapsed
// previews, between a preview and the expanded box, and vice-versa.
export const EDGE_GAP_PX = 30;
// Fallback height for a collapsed preview before it has rendered in the DOM
export const FALLBACK_PREVIEW_HEIGHT_PX = 46;
// Gap between the comment rail's right edge and the viewport edge
// (mirrors the 24px in .commentsPanelRail's `right` calc)
const RAIL_EDGE_GAP_PX = 24;
// Minimum right-side space needed to show full previews without overlapping content
const COMPACT_THRESHOLD_PX = COMMENT_DISPLAY_WIDTH_PX + RAIL_EDGE_GAP_PX;
const EMPTY_SET = new Set<string>();
// Use this value to tweak which table widths are considered wide enough to trigger compact mode
const EXTRA_WIDTH_BEFORE_CONSIDERED_WIDE_TABLE = 100;

export type PositionedThread = {
  threadId: string;
  top: number;
  left: number;
  isInTable: boolean;
  originId?: string | null;
};
export type PanelItem = {threadId: string; top: number; originId?: string | null};
type StackedPanelItem = PanelItem & {stackGroup: number};
type PreviewHeightGetter = (threadId: string) => number;

/** Toggle the `.highlighted` class on in-editor highlight spans for a given thread. */
const setHighlightHover = (threadId: string, hovered: boolean) => {
  const selector = `span[data-highlight-id~="${CSS.escape(threadId)}"]`;
  document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    if (!el.classList.contains(commentStyles.comment)) return;
    el.classList.toggle(commentStyles.highlighted, hovered);
  });
};

const isInWideTable = (el: HTMLElement, containerWidth: number): boolean => {
  if (el.matches('[data-heading-id][data-collapsed="true"]')) return false;

  const tableEl = el.closest<HTMLElement>('table, [data-planning-wide-table]');
  if (!tableEl) return false;
  return (
    tableEl.getBoundingClientRect().width >
    containerWidth + EXTRA_WIDTH_BEFORE_CONSIDERED_WIDE_TABLE
  );
};

/**
 * Find the first visible highlight rect for each thread relative to the container,
 * sorted top-to-bottom then left-to-right.
 */
const getThreadPositions = (
  containerEl: HTMLElement,
  threads: DocumentThread[],
  activeComment?: Pick<CommentState, 'threadId' | 'editorKey' | 'planningCommentOriginId'> | null
): PositionedThread[] => {
  const containerRect = containerEl.getBoundingClientRect();
  const items: Array<PositionedThread & {originalComment: HTMLElement}> = [];

  for (const thread of threads) {
    const editorKey = activeComment?.threadId === thread.id ? activeComment.editorKey : undefined;
    const originId =
      activeComment?.threadId === thread.id ? activeComment.planningCommentOriginId : undefined;
    const result = findCommentPositionAnchor(thread.id, containerEl, {
      includeHiddenFallback: false,
      ...(editorKey ? {editorKey} : {}),
      ...(originId ? {originId} : {}),
    });
    if (!result) continue;

    const anchorRect = result.anchor.getBoundingClientRect();
    items.push({
      threadId: thread.id,
      top: anchorRect.top - containerRect.top,
      left: anchorRect.left,
      isInTable: isInWideTable(result.anchor, containerRect.width),
      originId: getPlanningCommentOriginIdFromElement(result.originalComment),
      originalComment: result.originalComment,
    });
  }

  items.sort((a, b) => {
    const byPosition = a.top - b.top || a.left - b.left;
    if (byPosition !== 0) return byPosition;
    const pos = a.originalComment.compareDocumentPosition(b.originalComment);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });

  return items.map(({threadId, top, left, isInTable, originId}) => ({
    threadId,
    top,
    left,
    isInTable,
    originId,
  }));
};

/** Measure a collapsed preview's rendered height from the DOM. */
const getPreviewHeight = (threadId: string): number =>
  document.querySelector<HTMLElement>(`[data-comments-panel-thread-id="${CSS.escape(threadId)}"]`)
    ?.offsetHeight ?? FALLBACK_PREVIEW_HEIGHT_PX;

/** Cascade items top-down, enforcing EDGE_GAP_PX between bottom/top edges. */
const cascadeDown = (
  items: PanelItem[],
  getItemHeight: PreviewHeightGetter = getPreviewHeight
): PanelItem[] =>
  items.reduce<PanelItem[]>((acc, item) => {
    if (!acc.length) return [item];
    const prev = acc[acc.length - 1];
    const minTop = prev.top + getItemHeight(prev.threadId) + EDGE_GAP_PX;
    acc.push({...item, top: Math.max(item.top, minTop)});
    return acc;
  }, []);

/**
 * Layout: group same-line threads, then cascade with EDGE_GAP_PX spacing.
 *
 * When a comment is expanded, it anchors at its highlight position and
 * the rest of its same-line stack reflows around that anchored card. Without
 * this, opening a later item in a dense same-line stack leaves a hole where its
 * collapsed preview slot used to be.
 */
export const layoutPanelItems = (
  positions: PositionedThread[],
  activeThreadId?: string,
  activeCommentBoxHeight?: number,
  // getItemHeight is used to mock the height of a comment box for testing
  getItemHeight: PreviewHeightGetter = getPreviewHeight
): PanelItem[] => {
  // Group threads on the same line and stack left-to-right
  const stacked: StackedPanelItem[] = [];
  let i = 0;
  let stackGroup = 0;
  while (i < positions.length) {
    const groupTop = positions[i].top;
    let groupEnd = i + 1;
    while (
      groupEnd < positions.length &&
      Math.abs(positions[groupEnd].top - groupTop) <= SAME_LINE_TOLERANCE_PX
    ) {
      groupEnd++;
    }
    const group = positions.slice(i, groupEnd).sort((a, b) => a.left - b.left);
    group.forEach((item, index) => {
      stacked.push({
        threadId: item.threadId,
        top: groupTop + index * STACK_OFFSET_PX,
        stackGroup,
        ...(item.originId ? {originId: item.originId} : {}),
      });
    });
    i = groupEnd;
    stackGroup++;
  }

  const stackedPanelItems = stacked.map(({threadId, top, originId}) => ({
    threadId,
    top,
    ...(originId ? {originId} : {}),
  }));
  const activeIdx = activeThreadId
    ? stacked.findIndex((item) => item.threadId === activeThreadId)
    : -1;

  if (activeIdx === -1) {
    return cascadeDown(stackedPanelItems, getItemHeight);
  }

  const result: StackedPanelItem[] = stacked.map((item) => ({...item}));
  const activeItem = result[activeIdx];
  const activeStackGroup = result[activeIdx].stackGroup;
  const activeItemHeight = activeCommentBoxHeight ?? getItemHeight(activeItem.threadId);

  // Anchor the active item at its raw highlight position (before stacking)
  // to match where the toolbar renders the expanded box.
  const activeHighlight = positions.find((p) => p.threadId === activeThreadId);
  if (activeHighlight) {
    result[activeIdx].top = activeHighlight.top;
  }

  // Cascade downward — push items down only if they'd overlap
  for (let j = activeIdx + 1; j < result.length; j++) {
    const prev = result[j - 1];
    const prevH =
      prev.threadId === activeThreadId ? activeItemHeight : getItemHeight(prev.threadId);
    const minTop = prev.top + prevH + EDGE_GAP_PX;
    // Same-line stack offsets are artificial preview slots, not document positions.
    // When one of those previews becomes active, anchor it to the document and
    // reflow the rest of that same-line stack around the active card so we do
    // not preserve the active preview's old slot as an extra gap.
    result[j].top =
      result[j].stackGroup === activeStackGroup ? minTop : Math.max(result[j].top, minTop);
  }

  // Cascade upward — push items up only if they'd overlap
  for (let j = activeIdx - 1; j >= 0; j--) {
    const h = getItemHeight(result[j].threadId);
    result[j].top = Math.min(result[j].top, result[j + 1].top - h - EDGE_GAP_PX);
  }

  return result.map(({threadId, top, originId}) => ({
    threadId,
    top,
    ...(originId ? {originId} : {}),
  }));
};

export const CommentsPanel = ({
  containerEl,
  threads,
  activeThreadId,
  activeComment,
  onThreadSelect,
  onRightReserveChange,
}: {
  containerEl: HTMLElement | null;
  threads: DocumentThread[];
  activeThreadId?: string;
  activeComment?: Pick<
    CommentState,
    'threadId' | 'editorKey' | 'planningCommentOriginId' | 'activationId'
  > | null;
  onThreadSelect: (threadId: string, originId?: string | null) => void;
  onRightReserveChange?: (reserveRightPx: number) => void;
}) => {
  const threadById = useMemo(
    () => new Map(threads.map((thread) => [thread.id, thread])),
    [threads]
  );
  const [threadPositions, setThreadPositions] = useState<PositionedThread[]>([]);
  const [tableThreadIds, setTableThreadIds] = useState<Set<string>>(new Set());
  const [shouldCompactParagraphs, setShouldCompactParagraphs] = useState(false);
  const [activeBoxHeight, setActiveBoxHeight] = useState(0);

  const activeBoxHeightRef = useRef<number>(0);
  const setActiveBoxHeightValue = useCallback((height: number) => {
    activeBoxHeightRef.current = height;
    setActiveBoxHeight((previousHeight) => (previousHeight === height ? previousHeight : height));
  }, []);

  const panelItems = useMemo(
    () => layoutPanelItems(threadPositions, activeThreadId, activeBoxHeight || undefined),
    [activeBoxHeight, activeThreadId, threadPositions]
  );

  const compactThreadIds = useMemo(() => {
    if (activeThreadId) return EMPTY_SET;
    if (shouldCompactParagraphs) return new Set(threads.map((t) => t.id));
    return tableThreadIds;
  }, [activeThreadId, shouldCompactParagraphs, tableThreadIds, threads]);

  useEffect(() => {
    if (!onRightReserveChange) return;

    // Keep this reserve warm while the full active comment is open so closing it
    // does not briefly resize wide tables to the no-preview width.
    const hasCompactTablePreviews = activeThreadId
      ? tableThreadIds.size > 0
      : [...tableThreadIds].some((threadId) => compactThreadIds.has(threadId));
    const reserveRightPx = hasCompactTablePreviews ? COMMENT_DISPLAY_COMPACT_WIDTH_PX : 0;
    onRightReserveChange(reserveRightPx);
  }, [activeThreadId, compactThreadIds, onRightReserveChange, tableThreadIds]);

  useEffect(() => {
    if (!onRightReserveChange) return;
    return () => {
      onRightReserveChange(0);
    };
  }, [onRightReserveChange]);

  const hoveredThreadId = useCommentsPanelHoveredThread({
    containerEl,
    threads,
  });

  // Recalculate panel item positions whenever threads or DOM change
  const recalculateRef = useRef<() => void>();
  const scheduleRecalculateRef = useRef<() => void>();

  // Track the active comment box height so layout can reserve space for it.
  const activeCommentBoxRef = useRef<HTMLElement | null>(null);
  const activeCommentBoxResizeObserverRef = useRef<ResizeObserver | null>(null);
  const activeHostMutationObserverRef = useRef<MutationObserver | null>(null);
  const [activeHostEl, setActiveHostEl] = useState<HTMLDivElement | null>(null);

  const disconnectActiveCommentBoxResizeObserver = useCallback(
    (shouldRecalculate = false, shouldResetHeight = true) => {
      activeCommentBoxResizeObserverRef.current?.disconnect();
      activeCommentBoxResizeObserverRef.current = null;
      activeCommentBoxRef.current = null;

      if (shouldResetHeight) {
        setActiveBoxHeightValue(0);
      }

      if (shouldRecalculate) {
        scheduleRecalculateRef.current?.();
      }
    },
    [setActiveBoxHeightValue]
  );

  const disconnectActiveHostObservers = useCallback(
    (shouldRecalculate = false, shouldResetHeight = true) => {
      activeHostMutationObserverRef.current?.disconnect();
      activeHostMutationObserverRef.current = null;
      disconnectActiveCommentBoxResizeObserver(shouldRecalculate, shouldResetHeight);
    },
    [disconnectActiveCommentBoxResizeObserver]
  );

  const setActiveHostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        disconnectActiveHostObservers(true, false);
      }

      setActiveHostEl(node);
    },
    [disconnectActiveHostObservers]
  );

  useEffect(() => {
    return () => {
      disconnectActiveHostObservers(false);
    };
  }, [disconnectActiveHostObservers]);

  const observeActiveCommentBox = useCallback(
    (activeHost: HTMLElement) => {
      if (!activeHost.isConnected) {
        disconnectActiveHostObservers(true);
        return;
      }

      const box = activeHost.querySelector<HTMLElement>('[data-comment-box]');
      if (box === activeCommentBoxRef.current && box?.isConnected) return;

      disconnectActiveCommentBoxResizeObserver(false, Boolean(box));
      if (!box || !box.isConnected) {
        scheduleRecalculateRef.current?.();
        return;
      }

      activeCommentBoxRef.current = box;
      setActiveBoxHeightValue(box.getBoundingClientRect().height);

      const resizeObserver = new ResizeObserver((entries) => {
        const entry =
          entries.find((resizeEntry) => resizeEntry.target === activeCommentBoxRef.current) ??
          entries[0];
        const observedBox = entry?.target;

        if (
          !(observedBox instanceof HTMLElement) ||
          observedBox !== activeCommentBoxRef.current ||
          !observedBox.isConnected ||
          !activeHost.isConnected
        ) {
          disconnectActiveCommentBoxResizeObserver(true);
          return;
        }

        const height = entry.contentRect.height;
        if (height > 0 && height !== activeBoxHeightRef.current) {
          setActiveBoxHeightValue(height);
        }
      });

      activeCommentBoxResizeObserverRef.current = resizeObserver;
      resizeObserver.observe(box);
      scheduleRecalculateRef.current?.();
    },
    [
      disconnectActiveCommentBoxResizeObserver,
      disconnectActiveHostObservers,
      setActiveBoxHeightValue,
    ]
  );

  useEffect(() => {
    if (!activeThreadId || !activeHostEl || !activeHostEl.isConnected) {
      disconnectActiveHostObservers(true);
      return;
    }

    observeActiveCommentBox(activeHostEl);
    // The active comment is portaled into this host after the host mounts.
    const mutationObserver = new MutationObserver(() => observeActiveCommentBox(activeHostEl));
    activeHostMutationObserverRef.current = mutationObserver;
    mutationObserver.observe(activeHostEl, {
      childList: true,
      subtree: true,
    });

    return () => {
      if (activeHostMutationObserverRef.current === mutationObserver) {
        activeHostMutationObserverRef.current = null;
      }

      mutationObserver.disconnect();
      disconnectActiveCommentBoxResizeObserver(false, false);
    };
  }, [
    activeThreadId,
    activeHostEl,
    disconnectActiveCommentBoxResizeObserver,
    disconnectActiveHostObservers,
    observeActiveCommentBox,
  ]);

  useEffect(() => {
    if (!containerEl || !threads.length) {
      setThreadPositions([]);
      setTableThreadIds(new Set());
      setShouldCompactParagraphs(false);
      return;
    }

    let frameId: number | null = null;

    recalculateRef.current = () => {
      const positions = getThreadPositions(containerEl, threads, activeComment);
      setThreadPositions(positions);

      // Update table thread ids for compact mode
      const newTableIds = new Set(positions.filter((p) => p.isInTable).map((p) => p.threadId));
      setTableThreadIds((prev) =>
        prev.size === newTableIds.size && [...prev].every((id) => newTableIds.has(id))
          ? prev
          : newTableIds
      );

      // Update paragraph compact threshold
      const containerRect = containerEl.getBoundingClientRect();
      const rightSpace = window.innerWidth - containerRect.right;
      setShouldCompactParagraphs(rightSpace < COMPACT_THRESHOLD_PX);
    };

    scheduleRecalculateRef.current = () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => recalculateRef.current?.());
    };

    scheduleRecalculateRef.current();

    const observer = new MutationObserver(() => scheduleRecalculateRef.current?.());
    observer.observe(containerEl, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'aria-hidden',
        'data-collapsed',
        'data-highlight-id',
        COMMENT_EDITOR_KEY_ATTRIBUTE,
        PLANNING_COMMENT_ORIGIN_ATTR,
      ],
    });
    const handleResize = () => scheduleRecalculateRef.current?.();
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [containerEl, threads, activeComment]);

  if (!panelItems.length) {
    return null;
  }

  return (
    <div className={styles.commentsPanelRail} style={COMMENT_SIZING_VARS}>
      {panelItems.map(({threadId, top, originId}) => {
        const compact = compactThreadIds.has(threadId);
        const isActive = activeThreadId === threadId;

        if (isActive) {
          return (
            <div
              key={threadId}
              ref={setActiveHostRef}
              className={styles.commentsPanelActiveItem}
              style={{top: `${top}px`}}
              data-comments-panel-thread-id={threadId}
              data-comments-panel-active-thread-id={threadId}
            />
          );
        }

        return (
          <button
            key={threadId}
            type="button"
            className={styles.commentsPanelButton}
            onClick={() => {
              if (originId) {
                onThreadSelect(threadId, originId);
              } else {
                onThreadSelect(threadId);
              }
            }}
            onMouseEnter={() => setHighlightHover(threadId, true)}
            onMouseLeave={() => setHighlightHover(threadId, false)}
            onFocus={() => setHighlightHover(threadId, true)}
            onBlur={() => setHighlightHover(threadId, false)}
            style={{top: `${top}px`}}
            data-hovered={hoveredThreadId === threadId}
            data-compact={compact}
            data-comments-panel-thread-id={threadId}
            data-comments-panel-preview-thread-id={threadId}
            aria-label="Open comment thread"
          >
            <div className={styles.commentsPanelPreview}>
              <CommentDisplay mode="preview" thread={threadById.get(threadId)} compact={compact} />
            </div>
          </button>
        );
      })}
    </div>
  );
};
